import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as Endpoints from "../../entities/Endpoints";
import {
  fetchNotificationListApi,
  updateNotificationCountApi,
  fetchAnnouncementListApi,
} from "../../api/notificationApi";

export const getNotificationDetails = createAsyncThunk(
  Endpoints.GET_NOTIFICATION_DETAILS,
  async ({ coachId }, { rejectWithValue }) => {
    try {
      const response = await fetchNotificationListApi(coachId);
      if (!response.success) {
        return rejectWithValue(response?.data?.message);
      }
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "An unexpected error occurred."
      );
    }
  }
);

export const getAnnouncementList = createAsyncThunk(
  Endpoints.GET_ANNOUNCEMENT_LIST,
  async ({ coachId }, { rejectWithValue }) => {
    try {
      const response = await fetchAnnouncementListApi(coachId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "An unexpected error occurred."
      );
    }
  }
);

export const updateNotificationDetails = createAsyncThunk(
  Endpoints.UPDATE_NOTIFICATION_COUNT,
  async (
    { coachId, isMarkAllRead, notificationId },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await updateNotificationCountApi({
        coachId,
        isMarkAllRead,
        notificationId,
      });
      if (response.status === "Success") {
        await dispatch(getNotificationDetails({ coachId }));
      }
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "An unexpected error occurred."
      );
    }
  }
);

const notificationSlice = createSlice({
  name: "notifications",
  initialState: {
    notifications: [],
    // New state for announcements
    announcements: [],
    totalCount: 0,
    // New count for unread announcements
    announcementCount: 0,
    loading: false,
    // Separate loading state for announcements
    announcementsLoading: false,
    error: null,
    updateLoading: false,
  },
  reducers: {
    markAsRead: (state, action) => {
      const { id, isAnnouncement } = action.payload;
      if (isAnnouncement) {
        const announcement = state.announcements.find(
          (n) => n.notificationId === id
        );
        if (announcement && !announcement.isRead) {
          announcement.isRead = true;
          state.announcementCount =
            state.announcementCount > 0 ? state.announcementCount - 1 : 0;
        }
      } else {
        const notification = state.notifications.find((n) => n.id === id);
        if (notification && !notification.isRead) {
          notification.isRead = true;
          state.totalCount = state.totalCount > 0 ? state.totalCount - 1 : 0;
        }
      }
    },
    markAllAsRead: (state, action) => {
      const { isAnnouncement } = action.payload || {};

      if (isAnnouncement) {
        state.announcements.forEach((notification) => {
          if (!notification.isRead) {
            notification.isRead = true;
          }
        });
        state.announcementCount = 0;
      } else {
        state.notifications.forEach((notification) => {
          if (!notification.isRead) {
            notification.isRead = true;
          }
        });
        state.totalCount = 0;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Notifications
      .addCase(getNotificationDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNotificationDetails.fulfilled, (state, action) => {
        state.notifications = action.payload.data || [];
        state.totalCount = action.payload.count;
        state.loading = false;
      })
      .addCase(getNotificationDetails.rejected, (state, action) => {
        state.loading = false;
        state.notifications = [];
        state.error = action.payload || "An unexpected error occurred.";
      })

      // Announcements
      .addCase(getAnnouncementList.pending, (state) => {
        state.announcementsLoading = true;
        state.error = null;
      })
      .addCase(getAnnouncementList.fulfilled, (state, action) => {
        state.announcements = action.payload?.data || [];
        state.announcementCount = action.payload?.count || 0;
        state.announcementsLoading = false;
      })
      .addCase(getAnnouncementList.rejected, (state, action) => {
        state.announcementsLoading = false;
        state.announcements = [];
        state.error = action.payload || "Failed to load announcements";
      })

      // Update notifications
      .addCase(updateNotificationDetails.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateNotificationDetails.fulfilled, (state, action) => {
        const { notificationId, isMarkAllRead } = action.payload;

        if (isMarkAllRead) {
          state.notifications.forEach((n) => {
            if (!n.isRead) {
              n.isRead = true;
            }
          });
          state.totalCount = 0;
        } else if (notificationId) {
          const notification = state.notifications.find(
            (n) => n.id === notificationId
          );
          if (notification && !notification.isRead) {
            notification.isRead = true;
            state.totalCount = state.totalCount > 0 ? state.totalCount - 1 : 0;
          }
        }
        state.updateLoading = false;
      })
      .addCase(updateNotificationDetails.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload || "An unexpected error occurred.";
      });
  },
});

export const { markAsRead, markAllAsRead } = notificationSlice.actions;
export default notificationSlice.reducer;
